import React, { useState, useEffect, useCallback } from 'react';
import { Switch } from '@mui/material';
import { adminService } from '../../services/financeService';
import { checkAdminStatus } from '../../utils/authUtils';
import { FEATURE_GROUPS } from '../../utils/navigationUtils';
import '../financial/Financial.css';

const ALL_FEATURE_KEYS = FEATURE_GROUPS.flatMap(g => g.items.map(i => i.key));

const ROLE_STYLE = {
  SUPER_ADMIN: { background: 'var(--color-accent-light)', color: 'var(--color-accent-hover)' },
  ADMIN:       { background: 'var(--color-primary-light)', color: 'var(--color-primary)' },
  USER:        { background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' },
};

const badge = (text, styleMap, fallback = { background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }) => {
  const s = styleMap[text] || fallback;
  return (
    <span style={{ ...s, padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
};

const roleRank = (groupNames) => {
  if (groupNames?.includes('SUPER_ADMIN')) return 'SUPER_ADMIN';
  if (groupNames?.includes('ADMIN')) return 'ADMIN';
  return 'USER';
};

const emptyNewUser = (organizationId) => ({
  username: '', firstName: '', lastName: '', email: '', password: '',
  userCode: '', organizationId: organizationId || '', departmentId: '',
  groups: ['USER'], allowedDepartmentIds: [], active: true,
});

const UserManagement = () => {
  const userDataRaw = localStorage.getItem('userData');
  const currentUser = userDataRaw ? JSON.parse(userDataRaw) : null;
  const { isSuperAdmin } = checkAdminStatus(userDataRaw);
  const myOrgId = currentUser?.organization?.id || null;

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null); // null = "All organizations" (super admin only)
  const [departments, setDepartments] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState(emptyNewUser(myOrgId));
  const [createError, setCreateError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createFormDepartments, setCreateFormDepartments] = useState([]);

  const [editingUser, setEditingUser] = useState(null); // user row being role/scope-edited
  const [editGroups, setEditGroups] = useState([]);
  const [editDeptIds, setEditDeptIds] = useState([]);
  const [editOrgWide, setEditOrgWide] = useState(true);
  const [editAllowedFeatures, setEditAllowedFeatures] = useState(ALL_FEATURE_KEYS); // keys NOT blocked
  const [editError, setEditError] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [toggling, setToggling] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load organizations (super admin) + form-data (available roles) once ──
  useEffect(() => {
    adminService.getFormData().then(res => {
      if (res.success) setAvailableRoles(res.availableGroups || []);
    }).catch(() => {});
    if (isSuperAdmin) {
      adminService.getAllOrganizations().then(orgs => {
        setOrganizations(Array.isArray(orgs) ? orgs : []);
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  // ── Load departments for the org currently in view (for scope display/edit) ──
  useEffect(() => {
    const orgId = isSuperAdmin ? selectedOrgId : myOrgId;
    if (orgId) {
      adminService.getDepartmentsByOrganization(orgId).then(res => {
        if (res.success) setDepartments(res.departments || []);
      }).catch(() => {
        // Non-super-admin callers can't hit by-organization; fall back to their own org's list.
        adminService.getMyOrganizationDepartments().then(res => {
          if (res.success) setDepartments(res.departments || []);
        }).catch(() => {});
      });
    } else {
      setDepartments([]);
    }
  }, [isSuperAdmin, selectedOrgId, myOrgId]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const res = await adminService.listUsers(isSuperAdmin ? (selectedOrgId || undefined) : undefined, undefined);
      if (res.success) setUsers(res.users || []);
      else setPageError(res.message || 'Failed to load users');
    } catch (e) {
      setPageError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, selectedOrgId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const displayed = users.filter(u => {
    if (roleFilter && roleRank(u.groupNames) !== roleFilter) return false;
    if (statusFilter === 'ACTIVE' && !u.active) return false;
    if (statusFilter === 'INACTIVE' && u.active) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${u.fullName || ''} ${u.username || ''} ${u.email || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // ── Activate / deactivate ──
  const handleToggleActive = async (user) => {
    setToggling(user.id);
    try {
      await adminService.setUserActive(user.id, !user.active);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: !u.active } : u));
      showToast(`${user.fullName || user.username} is now ${!user.active ? 'active' : 'inactive'}.`);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setToggling(null);
    }
  };

  // ── Edit role/scope ──
  const openEdit = (user) => {
    setEditingUser(user);
    setEditGroups(Array.from(user.groupNames || []));
    setEditDeptIds(user.allowedDepartmentIds ? Array.from(user.allowedDepartmentIds) : []);
    setEditOrgWide(user.isOrgWideAdmin !== false);
    const blocked = user.blockedFeatures ? Array.from(user.blockedFeatures) : [];
    setEditAllowedFeatures(ALL_FEATURE_KEYS.filter(k => !blocked.includes(k)));
    setEditError(null);
  };

  const toggleEditRole = (role) => {
    setEditGroups(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const toggleEditDept = (deptId) => {
    setEditDeptIds(prev => prev.includes(deptId) ? prev.filter(id => id !== deptId) : [...prev, deptId]);
  };

  const toggleEditFeature = (key) => {
    setEditAllowedFeatures(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const saveEdit = async () => {
    if (editGroups.length === 0) { setEditError('Select at least one role'); return; }
    setEditSaving(true);
    setEditError(null);
    try {
      const isAdminRole = editGroups.includes('ADMIN');
      await adminService.updateUserRole(editingUser.id, {
        groups: editGroups,
        allowedDepartmentIds: isAdminRole && !editOrgWide ? editDeptIds : [],
      });
      const blockedFeatures = ALL_FEATURE_KEYS.filter(k => !editAllowedFeatures.includes(k));
      await adminService.updateFeatureAccess(editingUser.id, blockedFeatures);
      showToast(`Updated ${editingUser.fullName || editingUser.username}.`);
      setEditingUser(null);
      fetchUsers();
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditSaving(false);
    }
  };

  // ── Create user ──
  const openCreate = () => {
    setNewUser(emptyNewUser(isSuperAdmin ? '' : myOrgId));
    setCreateError(null);
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
      setCreateError('All fields are required'); return;
    }
    if (!newUser.organizationId) { setCreateError('Organization is required'); return; }
    const isAdminRole = newUser.groups.includes('ADMIN');
    if (!isAdminRole && !newUser.departmentId) { setCreateError('Department is required'); return; }
    setCreateLoading(true);
    setCreateError(null);
    try {
      const payload = {
        ...newUser,
        organizationId: Number(newUser.organizationId),
        departmentId: newUser.departmentId ? Number(newUser.departmentId) : null,
      };
      const res = await adminService.createUser(payload);
      showToast(`Created user ${res.user?.username || newUser.username}.`);
      setShowCreate(false);
      fetchUsers();
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreateLoading(false);
    }
  };

  // Departments offered in the Create User form must come from whichever organization is
  // picked INSIDE that form (newUser.organizationId) — NOT the page-level org switcher
  // (selectedOrgId). Those two are independent: a Super Admin can have the switcher on "All
  // Organizations" (or a different org entirely) while creating a user for some other org, and
  // the old code returned an empty list whenever they didn't match — the new org's real
  // departments existed in the database the whole time, they just never got fetched. Bug
  // caught live 2026-09-10: a freshly created org + department didn't show up when creating a
  // user for it, because the switcher wasn't also pointed at that org.
  useEffect(() => {
    if (!newUser.organizationId) { setCreateFormDepartments([]); return; }
    let cancelled = false;
    adminService.getDepartmentsByOrganization(newUser.organizationId).then(res => {
      if (!cancelled && res.success) setCreateFormDepartments(res.departments || []);
    }).catch(() => {
      if (!cancelled) setCreateFormDepartments([]);
    });
    return () => { cancelled = true; };
  }, [newUser.organizationId]);

  const orgSelectDepartments = createFormDepartments;

  return (
    <div className="financial-container">
      {/* ── Header ── */}
      <div className="financial-header">
        <div>
          <h2 className="financial-title">User Management</h2>
          <p className="financial-subtitle">
            {isSuperAdmin
              ? 'Manage every user, across every organization.'
              : 'Manage the people in your organization.'}
          </p>
        </div>
        <button className="btn-primary" onClick={openCreate}>+ New User</button>
      </div>

      {/* ── Org switcher (super admin only) ── */}
      {isSuperAdmin && (
        <div className="tab-container">
          <button
            className={`tab-button ${selectedOrgId === null ? 'active' : ''}`}
            onClick={() => setSelectedOrgId(null)}
          >
            All Organizations
          </button>
          {organizations.map(org => (
            <button
              key={org.id}
              className={`tab-button ${selectedOrgId === org.id ? 'active' : ''}`}
              onClick={() => setSelectedOrgId(org.id)}
            >
              {org.name}
            </button>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
        <input
          className="form-control"
          style={{ maxWidth: '240px' }}
          placeholder="Search name, username, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="form-control" style={{ maxWidth: '160px' }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          <option value="SUPER_ADMIN">Super Admin</option>
          <option value="ADMIN">Admin</option>
          <option value="USER">User</option>
        </select>
        <select className="form-control" style={{ maxWidth: '160px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      {pageError && (
        <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {pageError}
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="loading-state">Loading users…</div>
      ) : displayed.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">—</div>
          <h3>No users found</h3>
          <p>Click "New User" to add the first one, or adjust your filters.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table responsive-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username / Email</th>
                {(!isSuperAdmin || selectedOrgId === null) && <th>Organization</th>}
                <th>Role</th>
                <th>Scope</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(u => {
                const role = roleRank(u.groupNames);
                return (
                  <tr key={u.id}>
                    <td data-label="Name">{u.fullName || '—'}</td>
                    <td data-label="Username / Email">
                      <div>{u.username}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{u.email}</div>
                    </td>
                    {(!isSuperAdmin || selectedOrgId === null) && <td data-label="Organization">{u.organizationName || '—'}</td>}
                    <td data-label="Role">{badge(role, ROLE_STYLE)}</td>
                    <td data-label="Scope">
                      {role === 'SUPER_ADMIN' ? (
                        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>All organizations</span>
                      ) : role === 'ADMIN' ? (
                        u.isOrgWideAdmin ? (
                          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Whole organization</span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {(u.allowedDepartmentNames || []).map(name => (
                              <span key={name} style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', padding: '1px 8px', borderRadius: 9999, fontSize: 10 }}>
                                {name}
                              </span>
                            ))}
                          </div>
                        )
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{u.departmentName || '—'}</span>
                      )}
                      {u.blockedFeatures?.length > 0 && (
                        <div style={{ fontSize: 10, color: 'var(--color-warning)', marginTop: 4 }}>
                          {u.blockedFeatures.length} feature{u.blockedFeatures.length === 1 ? '' : 's'} blocked
                        </div>
                      )}
                    </td>
                    <td data-label="Status">{badge(u.active ? 'Active' : 'Inactive', {
                      Active: { background: 'var(--color-success-light)', color: 'var(--color-success)' },
                      Inactive: { background: 'var(--color-error-light)', color: 'var(--color-error)' },
                    })}</td>
                    <td data-label="Actions" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: 12, marginRight: 6 }}
                        onClick={() => openEdit(u)}>
                        Edit Role
                      </button>
                      <button className="btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                        disabled={toggling === u.id}
                        onClick={() => handleToggleActive(u)}>
                        {toggling === u.id ? '…' : u.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit Role modal ── */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => !editSaving && setEditingUser(null)}>
          <div className="modal-content edit-user-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Edit — {editingUser.fullName || editingUser.username}</h3>

            <div className="edit-user-section">
              <div className="edit-user-section-title">Role</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {availableRoles.map(role => (
                  <button key={role} type="button"
                    className={`btn ${editGroups.includes(role) ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '6px 16px', fontSize: 13, borderRadius: 20 }}
                    onClick={() => toggleEditRole(role)}>
                    {role}
                  </button>
                ))}
              </div>
            </div>

            {editGroups.includes('ADMIN') && (
              <div className="edit-user-section">
                <div className="edit-user-section-title">Scope</div>
                <label className="feature-row">
                  <span>Manages the whole organization</span>
                  <Switch checked={editOrgWide} onChange={e => setEditOrgWide(e.target.checked)} size="small" />
                </label>
                {!editOrgWide && (
                  <div style={{ background: 'var(--color-bg-tertiary)', borderRadius: 8, padding: '4px 12px', marginTop: 4 }}>
                    {departments.length === 0 ? (
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>No departments found for this organization.</span>
                    ) : (
                      departments.map(d => (
                        <label key={d.id} className="feature-row" style={{ padding: '6px 0' }}>
                          <span>{d.name}</span>
                          <Switch checked={editDeptIds.includes(d.id)} onChange={() => toggleEditDept(d.id)} size="small" />
                        </label>
                      ))
                    )}
                  </div>
                )}
                <p className="edit-user-hint">A department-scoped Admin only sees and manages users in their selected department(s).</p>
              </div>
            )}

            <div className="edit-user-section">
              <div className="edit-user-section-title">Feature Access</div>
              <p className="edit-user-hint" style={{ marginTop: 0 }}>Turn off individual pages for this person, regardless of their role.</p>
              <div className="feature-access-groups">
                {FEATURE_GROUPS.map(group => (
                  <div key={group.label} className="feature-access-group">
                    <div className="feature-access-group-title">{group.label}</div>
                    {group.items.map(item => (
                      <label key={item.key} className="feature-row">
                        <span>{item.label}</span>
                        <Switch checked={editAllowedFeatures.includes(item.key)}
                          onChange={() => toggleEditFeature(item.key)} size="small" />
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {editError && <div className="form-error">{editError}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn-secondary" onClick={() => setEditingUser(null)} disabled={editSaving}>Cancel</button>
              <button className="btn-primary" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create User modal ── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => !createLoading && setShowCreate(false)}>
          <div className="modal-content" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>New user</h3>
            <form onSubmit={submitCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">First name</label>
                  <input className="form-control" value={newUser.firstName} onChange={e => setNewUser(u => ({ ...u, firstName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last name</label>
                  <input className="form-control" value={newUser.lastName} onChange={e => setNewUser(u => ({ ...u, lastName: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Username</label>
                <input className="form-control" value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input type="password" className="form-control" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
              </div>
              {isSuperAdmin && (
                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <select className="form-control" value={newUser.organizationId}
                    onChange={e => setNewUser(u => ({ ...u, organizationId: e.target.value, departmentId: '' }))}>
                    <option value="">Select organization</option>
                    {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Role</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {availableRoles.map(role => (
                    <button key={role} type="button"
                      className={`btn ${newUser.groups.includes(role) ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ padding: '6px 16px', fontSize: 13, borderRadius: 20 }}
                      onClick={() => setNewUser(u => ({
                        ...u,
                        groups: u.groups.includes(role) ? u.groups.filter(r => r !== role) : [...u.groups, role],
                      }))}>
                      {role}
                    </button>
                  ))}
                </div>
              </div>
              {!newUser.groups.includes('ADMIN') ? (
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <select className="form-control" value={newUser.departmentId}
                    onChange={e => setNewUser(u => ({ ...u, departmentId: e.target.value }))}>
                    <option value="">Select department</option>
                    {orgSelectDepartments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Departments this Admin manages (leave empty for whole organization)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 6 }}>
                    {orgSelectDepartments.map(d => (
                      <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input type="checkbox" checked={newUser.allowedDepartmentIds.includes(d.id)}
                          onChange={() => setNewUser(u => ({
                            ...u,
                            allowedDepartmentIds: u.allowedDepartmentIds.includes(d.id)
                              ? u.allowedDepartmentIds.filter(id => id !== d.id)
                              : [...u.allowedDepartmentIds, d.id],
                          }))} />
                        {d.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {createError && <div className="form-error">{createError}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)} disabled={createLoading}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={createLoading}>
                  {createLoading ? 'Creating…' : 'Create user'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          border: `1px solid ${toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)'}`,
          padding: '14px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
